import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { Rule } from 'lib/constraintsChecker/Rule';
import {
  ActionConstraint,
  ActionConstraintType,
} from 'lib/constraintsChecker/ActionConstraint';
import { ActionMessage } from 'lib/constraintsChecker/ActionMessage';
import cloneDeep from 'lodash/cloneDeep';
import { CustomRule } from 'lib/constraintsChecker/constants';
import { ActionMessageResponse } from 'lib/constraintsChecker/ActionMessageResponse';
import { Furniture } from '../modelBuilder/entities/Furniture';

export enum FurnitureEditorStatus {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Cancel = 'cancel',
  Modified = 'modified',
  Init = 'init',
}

export enum FurnitureEditorContext {
  webShop = 'webshop',
  sales = 'shops',
  projects = 'projects',
}

export class FurnitureEditor {
  cabinet?: Cabinet;
  cabinetOrigin: Cabinet;
  status: FurnitureEditorStatus;
  message?: ActionMessage;
  responses: ActionMessageResponse[];
  rules: Rule[];
  context: FurnitureEditorContext;
  customMode: boolean;
  proposeUndo: boolean;
  modal: boolean;
  image?: string;
  noOK: boolean;
  public current: any;
  public next: any;

  constructor(
    furnitureOrigin: Furniture,
    selectedIdCabinet: string | null,
    rules: Rule[],
    context = FurnitureEditorContext.webShop,
    customMode = false,
  ) {
    let cabinetSelected: Cabinet | null = null;
    if (selectedIdCabinet) {
      cabinetSelected = furnitureOrigin.get(selectedIdCabinet);
    }
    if (cabinetSelected) {
      this.cabinetOrigin = cabinetSelected;
    } else {
      this.cabinetOrigin = furnitureOrigin.first;
    }
    this.customMode = customMode;
    this.status = FurnitureEditorStatus.Init;
    this.rules = rules;
    this.context = context;

    this.responses = [];
    this.proposeUndo = false;
    this.modal = false;
    this.noOK = false;
  }

  validateRules(cabinetNext: Cabinet): boolean {
    this.checkRules(cabinetNext);
    if (this.cabinet && !this.cabinet.isValid())
      throw Error('Rules invalidate the model');

    return !!(this.cabinet && this.status !== FurnitureEditorStatus.Init);
  }

  applyAction(
    action: ActionConstraint,
    cabinetNext: Cabinet,
    next: Cabinet | Shelf,
  ): Cabinet {
    this.next = next;

    //TODO refactor and manage multiple messages properly
    //TODO have confirmation messages after responses

    switch (action.type) {
      case ActionConstraintType.Cancel:
        if (action.message) this.message = action.message;
        this.status = FurnitureEditorStatus.Cancel;
        return this.cabinetOrigin;
      case ActionConstraintType.Warning:
        if (action.message) {
          if (this.message) {
            if (this.message.content.indexOf(action.message.content) === -1)
              this.message.content += `<br>${action.message.content}`;
          } else this.message = new ActionMessage(-1, action.message.content);
        }
        if (!this.message) {
          throw new Error('There must be a message in "Warning" action');
        }
        this.status = FurnitureEditorStatus.Warning;
        break;
      case ActionConstraintType.Info:
        if (action.message) {
          if (this.message) {
            if (this.message.content.indexOf(action.message.content) === -1)
              this.message.content += `<br>${action.message.content}`;
          } else this.message = new ActionMessage(-1, action.message.content);
        }

        if (!this.message) {
          throw new Error('There must be a message in "Info" action');
        }
        break;
      case ActionConstraintType.Modify:
        if (action.message) {
          if (this.message) {
            if (this.message.content.indexOf(action.message.content) === -1)
              this.message.content += `<br>${action.message.content}`;
          } else this.message = new ActionMessage(-1, action.message.content);
        }

        this.status = FurnitureEditorStatus.Modified;
        if (action.change) {
          eval(action.change);
        } else throw new Error('There must be a change in "Modify" action');

        break;
      default:
        throw new Error('Action type unknown !');
    }
    if (action.proposeUndo) this.proposeUndo = true;
    if (action.modal) this.modal = true;
    if (action.noOK) this.noOK = true;
    if (action.image) this.image = action.image;
    return cabinetNext;
  }

  canApplyRule(rule: Rule): boolean {
    return (
      (this.context !== FurnitureEditorContext.webShop || rule.webshop) &&
      (this.context !== FurnitureEditorContext.sales || rule.shops) &&
      (this.context !== FurnitureEditorContext.projects || rule.projects) &&
      (!this.customMode || rule.custom !== CustomRule.onlyNotCustom) &&
      (this.customMode || rule.custom !== CustomRule.onlyCustom)
    );
  }

  checkRule(cabinetNext: Cabinet, rule: Rule): Cabinet {
    /* eslint-disable @typescript-eslint/no-unused-vars */

    if (!this.canApplyRule(rule)) return cabinetNext;

    if (rule.scope === Cabinet) {
      // Compute the responses of the rule for Cabinet scope
      this.current = this.cabinetOrigin;
      this.next = cabinetNext;

      // Check if this rule apply if currentCondition and nextCondition
      // in the rule are true for cabinetNext
      if (eval(rule.currentCondition) && eval(rule.nextCondition)) {
        // Compute the responses of the rule
        // save the cabinetNext
        if (rule.action.responses) {
          // Apply the rule response on a copy and save the compute cabinet model
          // so when user want to apply action we replace cabinet model by this pre-computed model
          const tmpNext = this.next;
          for (const response of rule.action.responses) {
            // next is a copy to start computing the response from a clean model
            this.next = tmpNext.clone();
            // Eval the possible change and the response which will modify cabinetNext
            if (rule.action.change) eval(rule.action.change);
            eval(response.action);
            // console.log('eval action', rule);
            // Save the pre-computed model
            this.responses.push({ ...response, cabinet: this.next });
          }
          // Restore
          this.next = tmpNext;
        }
        // Apply the rule action
        return this.applyAction(rule.action, cabinetNext, cabinetNext);
      }
    } else if (
      rule.scope === Shelf &&
      cabinetNext.shelves.length === this.cabinetOrigin.shelves.length
    ) {
      // Compute the responses of the rule for Shelf scope
      const tmpNext = cabinetNext;
      if (rule.action.responses) {
        for (const response of rule.action.responses) {
          cabinetNext = tmpNext.clone();
          let cabinetIsEdited = false;
          for (let i = 0; i < this.cabinetOrigin.shelves.length; i++) {
            this.current = this.cabinetOrigin.shelves[i];
            this.next = cabinetNext.shelves[i];
            if (
              eval(rule.currentCondition) &&
              eval(rule.nextCondition) &&
              this.status !== FurnitureEditorStatus.Cancel
            ) {
              cabinetIsEdited = true;
              if (rule.action.change) eval(rule.action.change);
              eval(response.action);
              // console.log('eval action', rule);
            }
          }
          if (cabinetIsEdited)
            this.responses.push({ ...response, cabinet: cabinetNext });
        }
      }
      // Restore the cabinetNext to avoid mix up with responses
      cabinetNext = tmpNext;

      // Apply rule
      for (let i = 0; i < this.cabinetOrigin.shelves.length; i++) {
        this.current = this.cabinetOrigin.shelves[i];
        this.next = cabinetNext.shelves[i];
        // Evaluate the code of the rules for the current shelf
        if (
          eval(rule.currentCondition) &&
          eval(rule.nextCondition) &&
          this.status !== FurnitureEditorStatus.Cancel
        ) {
          cabinetNext = this.applyAction(
            rule.action,
            cabinetNext,
            cabinetNext.shelves[i],
          );
        }
      }
    }

    return cabinetNext;
  }

  checkRules(cabinetNext: Cabinet) {
    let cabinetModified: Cabinet = cabinetNext;
    for (const rule of this.rules) {
      this.checkRule(cabinetModified, rule);
      if (this.status === FurnitureEditorStatus.Cancel) return;
      // Reapply rule if reverse is true
      if (rule.reverse) {
        const ruleReversed = cloneDeep(rule);
        [ruleReversed.currentCondition, ruleReversed.nextCondition] = [
          ruleReversed.nextCondition,
          ruleReversed.currentCondition,
        ];
        ruleReversed.currentCondition = ruleReversed.currentCondition
          .replace('current.', 'nexttmp.')
          .replace('next.', 'current.')
          .replace('nexttmp.', 'next.');
        ruleReversed.nextCondition = ruleReversed.nextCondition
          .replace('current.', 'nexttmp.')
          .replace('next.', 'current.')
          .replace('nexttmp.', 'next.');
        cabinetModified = this.checkRule(cabinetModified, ruleReversed);
      }
    }
    if (this.status === FurnitureEditorStatus.Init)
      this.status = FurnitureEditorStatus.Success;
    this.cabinet = cabinetModified;
  }
}
